﻿import { FunctionComponent, useCallback, useMemo, useRef } from "react";
import { Confirm, ConfirmProps } from "../components/Confirm";

type UseConfirm = { confirm: () => Promise<boolean>; ConfirmDialog: FunctionComponent };
type UseConfirmOpts = Omit<ConfirmProps, "openFnRef" | "onResultRef">;

/**
 * This hook is used to create a confirm dialog. It returns a function that can be called to open the dialog and await the outcome of the user's input.
 * It makes confirmation dialogs much simpler to implement.
 * @param opts
 */
export function useConfirm(opts?: UseConfirmOpts): UseConfirm {
    const resolve = useRef<((value: boolean | PromiseLike<boolean>) => void) | undefined>();
    const promise = useRef<Promise<boolean>>(Promise.resolve(false));
    const open = useRef<() => void>();

    const resetPromise = useCallback(() => {
        promise.current = new Promise(res => {
            resolve.current = res;
        });
    }, []);

    const confirm = useCallback(() => {
        resetPromise();

        if (open.current) {
            open.current();
        }
        return promise.current;
    }, [resetPromise]);

    const ConfirmDialog: FunctionComponent = () => useMemo(() => <Confirm {...opts} openFnRef={open} onResultRef={resolve} />, []);

    return {
        confirm,
        ConfirmDialog,
    };
}
